import { useState, useEffect, useRef } from 'react'
import { X, Check, Sparkles, Star, Users } from 'lucide-react'

export default function LifetimeCheckoutModal({ isOpen, onClose, onSuccess, userEmail }) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [processingPayment, setProcessingPayment] = useState(false)
    const [spotsData, setSpotsData] = useState({ remaining: 100, soldOut: false, percentUsed: 0 })
    const paypalRef = useRef(null)
    const paypalButtonRendered = useRef(false)

    // Use VITE_PAYPAL_CLIENT_ID from environment - user should set this to their live client ID
    const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID

    const features = [
        'Unlimited AI-powered suggestions',
        'Advanced cliché detection',
        'Custom style guides',
        'Priority support',
        'Chrome extension Pro features',
        'Detailed analytics & reports',
        'All future Pro features',
        'Never pay again!'
    ]

    // Fetch remaining spots when modal opens
    useEffect(() => {
        if (!isOpen) return

        const fetchSpots = async () => {
            try {
                const response = await fetch('/api/payments/lifetime-spots')
                if (response.ok) {
                    const data = await response.json()
                    setSpotsData(data)
                }
            } catch (err) {
                console.error('Error fetching spots:', err)
            }
        }

        fetchSpots()
    }, [isOpen])

    // Load PayPal script
    useEffect(() => {
        if (!isOpen) return

        setLoading(true)
        setError(null)
        paypalButtonRendered.current = false

        const loadPayPalScript = () => {
            const existingScript = document.getElementById('paypal-sdk-lifetime')
            if (existingScript) {
                renderPayPalButton()
                return
            }

            const script = document.createElement('script')
            script.id = 'paypal-sdk-lifetime'
            script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&components=buttons`
            script.setAttribute('data-sdk-integration-source', 'button-factory')

            script.onload = () => {
                renderPayPalButton()
            }

            script.onerror = () => {
                setError('Failed to load PayPal. Please try again.')
                setLoading(false)
            }

            document.body.appendChild(script)
        }

        const renderPayPalButton = () => {
            if (!paypalRef.current) return
            if (!window.paypal) {
                setTimeout(renderPayPalButton, 100)
                return
            }

            paypalRef.current.innerHTML = ''
            paypalButtonRendered.current = false

            try {
                window.paypal.Buttons({
                    style: {
                        shape: 'pill',
                        color: 'gold',
                        layout: 'vertical',
                        label: 'pay'
                    },
                    createOrder: async function (data, actions) {
                        // Create PayPal order for one-time payment
                        return actions.order.create({
                            purchase_units: [{
                                description: 'InkSpot Pro - Founding Member Lifetime Access',
                                amount: {
                                    value: '29.00',
                                    currency_code: 'USD'
                                }
                            }]
                        })
                    },
                    onApprove: async function (data, actions) {
                        setProcessingPayment(true)
                        try {
                            // Capture the payment
                            const details = await actions.order.capture()
                            console.log('Payment captured:', details)

                            // Call our API to grant lifetime access
                            const response = await fetch('/api/payments/capture-lifetime', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    orderId: data.orderID,
                                    email: userEmail || details.payer?.email_address
                                })
                            })

                            if (response.ok) {
                                if (onSuccess) onSuccess()
                                onClose()
                                alert('🎉 Welcome to InkSpot, Founding Member! You now have lifetime Pro access!')
                                window.location.reload()
                            } else {
                                throw new Error('Failed to process payment')
                            }
                        } catch (err) {
                            console.error('Error processing payment:', err)
                            setError('Payment received but activation failed. Please contact support.')
                        } finally {
                            setProcessingPayment(false)
                        }
                    },
                    onError: function (err) {
                        console.error('PayPal error:', err)
                        setError('Payment failed. Please try again.')
                    }
                }).render(paypalRef.current)

                paypalButtonRendered.current = true
                setLoading(false)
            } catch (err) {
                console.error('Error rendering PayPal button:', err)
                setError('Failed to initialize PayPal. Please try again.')
                setLoading(false)
            }
        }

        setTimeout(loadPayPalScript, 100)

        return () => {
            paypalButtonRendered.current = false
        }
    }, [isOpen, PAYPAL_CLIENT_ID, onClose, onSuccess, userEmail])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg glass-card p-8 animate-scale-in max-h-[90vh] overflow-y-auto">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Header */}
                <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                        <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                        Founding Member
                    </h2>
                    <p className="text-white/60">
                        Lifetime Pro access - pay once, use forever
                    </p>
                </div>

                {/* Badge */}
                <div className="flex flex-col items-center gap-2 mb-4">
                    <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-yellow-400 text-sm font-medium">
                        🔥 Limited Time Offer
                    </span>
                    {!spotsData.soldOut ? (
                        <div className="flex items-center gap-2 text-white/60 text-sm">
                            <Users className="w-4 h-4" />
                            <span>
                                <span className="text-primary-400 font-bold">{spotsData.remaining}</span> of 100 spots remaining
                            </span>
                        </div>
                    ) : (
                        <div className="text-red-400 text-sm font-medium">
                            ❌ All spots claimed!
                        </div>
                    )}
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-2">
                        <span className="text-5xl font-bold text-white">$29</span>
                        <span className="text-white/50">one-time</span>
                    </div>
                    <p className="text-sm text-green-400 mt-2">
                        ✓ Never pay monthly fees again
                    </p>
                    <p className="text-xs text-white/40 mt-1">
                        Valued at $36/year • Pay once, use forever
                    </p>
                </div>

                {/* Features */}
                <div className="space-y-2 mb-8">
                    {features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                                <Check className="w-3 h-3 text-green-400" />
                            </div>
                            <span className="text-white/80 text-sm">{feature}</span>
                        </div>
                    ))}
                </div>

                {/* PayPal Button Container */}
                <div className="min-h-[50px]">
                    {loading && (
                        <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-3 text-white/60">Loading payment options...</span>
                        </div>
                    )}

                    {processingPayment && (
                        <div className="flex items-center justify-center py-4">
                            <div className="w-6 h-6 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-3 text-white/60">Activating your lifetime access...</span>
                        </div>
                    )}

                    {error && (
                        <div className="text-center py-4">
                            <p className="text-red-400 mb-2">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="text-primary-400 hover:underline"
                            >
                                Refresh page
                            </button>
                        </div>
                    )}

                    <div ref={paypalRef} className={loading || processingPayment ? 'hidden' : ''} />
                </div>

                {/* Security Note */}
                <p className="text-center text-xs text-white/40 mt-4">
                    🔒 Secure one-time payment powered by PayPal
                </p>
            </div>
        </div>
    )
}
